import { tryOnMounted, useDebounceFn, useMutationObserver } from '@vueuse/core';
import { type MaybeRef, type Ref, ref, unref } from 'vue';

/**
 * Observe if a given HTML element is empty or not. The function will watch
 * for changes in the element's DOM subtree and change the it's value
 * accordingly.
 *
 * @param element The element to observe.
 * @returns A reactive boolean indicating if the element is empty or not.
 * @example
 * <script setup lang="ts">
 * const ref = ref<HTMLElement>()
 * const isEmpty = useElementEmptiness(ref) => // => Ref { value: true }
 * </script>
 *
 * <template>
 *  <div ref="ref">
 *    <p v-if="false">This element won't be rendered.</p>
 *  </div>
 *
 *  <p v-if="!isEmpty">The element is not empty.</p>
 * </template>
 */
export function useElementEmptiness(element: MaybeRef<HTMLElement | null>): Ref<boolean> {
  const isElementEmpty = ref(false);

  // --- Function to compute the emptiness state of the element.
  function callback() {
    const elementValue = unref(element);
    if (!elementValue) {
      isElementEmpty.value = true;
      return isElementEmpty.value;
    }

    // --- Check if the innerHTML is empty or if the element is hidden.
    const innerHTML = elementValue?.innerHTML?.replace(/<!--(.*?)-->/gm, '')?.trim();

    isElementEmpty.value = Boolean(!innerHTML);
  }

  // --- Debounce the callback to avoid multiple calls per tick.
  const callbackDebounced = useDebounceFn(callback, 100);

  // --- Check for the emptiness state on mount and on mutation.
  tryOnMounted(callback);
  useMutationObserver(element, callbackDebounced, { childList: true });

  // --- Return the emptiness state.
  return isElementEmpty;
}
